import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "../../components/ui-components/Toast";
import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { secondaryButtonCls, destructiveButtonCls } from "../../components/ui-components/buttonStyles.js";
import FormButton from "../../components/form-components/FormButton";
import Modal from "../../components/ui-components/Modal";
import Skeleton from "../../components/ui-components/Skeleton";
import ConnectionStatusBadge from "./components/ConnectionStatusBadge";
import ValidationHistory from "./components/ValidationHistory";
import { getSourceConnection, deleteSourceConnection, validateSourceConnection, getValidationHistory } from "./api";
import { getSourceType } from "./sourceTypes";
import { maskCredential } from "./utils";
import { formatRelativeTime, formatDuration, cronToHuman } from "../data-imports/utils";

const StatusCard = ({ label, children, subtext }) => (
  <div className="bg-card border border-card-bd rounded-card p-4">
    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">{label}</div>
    {children}
    {subtext && <div className="text-xs text-text-2 mt-1">{subtext}</div>}
  </div>
);

const ConnectionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCredential, setShowCredential] = useState(false);

  const { data: connection, isLoading, isError } = useQuery({
    queryKey: ["source-connection", id],
    queryFn: () => getSourceConnection(id),
  });

  const { data: validationHistory } = useQuery({
    queryKey: ["validation-history", id],
    queryFn: () => getValidationHistory(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSourceConnection(id),
    onSuccess: () => {
      toast.success("Connection deleted");
      queryClient.invalidateQueries({ queryKey: ["source-connections"] });
      navigate("/source-connections");
    },
    onError: (err) => toast.error(err?.message || "Failed to delete"),
  });

  const revalidateMutation = useMutation({
    mutationFn: () => validateSourceConnection(id),
    onSuccess: () => {
      toast.success("Validation started");
      queryClient.invalidateQueries({ queryKey: ["source-connection", id] });
      queryClient.invalidateQueries({ queryKey: ["validation-history", id] });
    },
    onError: (err) => toast.error(err?.message || "Failed to start validation"),
  });

  useSetHeaderRecordName(connection?.connection_name || null);

  if (isLoading) {
    return (
      <div className="p-8">
        <Skeleton height={64} className="mb-8" />
        <Skeleton height={200} />
      </div>
    );
  }

  if (isError || !connection) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-text mb-2">Connection not found</h2>
        <FormButton title="Back to Source Connections" onClick={() => navigate("/source-connections")} />
      </div>
    );
  }

  const sourceType = getSourceType(connection.source_type);
  const dataTypeLabels = (connection.data_types || []).map(
    (dtId) => sourceType?.dataTypes.find((d) => d.id === dtId)?.label || dtId
  );

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Title + status live in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            {connection.connection_name}
          </div>
          <ConnectionStatusBadge status={connection.credential_status} />
        </div>
      </HeaderPortal>

      {/* Action buttons live in the global top bar (next to the tab switcher), like /control-policies/approval-policies/:id. */}
      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className={secondaryButtonCls}
            onClick={() => revalidateMutation.mutate()}
            disabled={revalidateMutation.isPending}
          >
            {revalidateMutation.isPending ? "Validating…" : "Re-validate"}
          </button>
          <button
            type="button"
            className={destructiveButtonCls}
            onClick={() => setShowDeleteModal(true)}
          >
            Delete
          </button>
        </div>
      </HeaderPortal>

      <div className="p-8 flex-1 overflow-auto">
        {/* Source info line */}
        <div className="text-sm text-text-2 mb-6">
          {sourceType?.name || connection.source_type} · {sourceType?.credentialLabel || connection.auth_method} · Added {new Date(connection.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatusCard label="Credential Status" subtext={connection.last_validated_at ? `Checked ${formatRelativeTime(connection.last_validated_at)}` : null}>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full inline-block ${
                connection.credential_status === "verified" ? "bg-success" :
                connection.credential_status === "failed" ? "bg-danger" : "bg-warning"
              }`} />
              <span className="text-base font-semibold text-text capitalize">{connection.credential_status}</span>
            </div>
          </StatusCard>
          <StatusCard label="Import Job" subtext={connection.schedule_expression ? cronToHuman(connection.schedule_expression) : null}>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full inline-block ${
                connection.import_job_status === "active" ? "bg-success" : "bg-text-3"
              }`} />
              <span className="text-base font-semibold text-text capitalize">{connection.import_job_status || "—"}</span>
            </div>
          </StatusCard>
          <StatusCard label="Last Sync" subtext={connection.last_execution_time_ms ? `Completed in ${formatDuration(connection.last_execution_time_ms)}` : null}>
            <div className="text-base font-semibold text-text">{formatRelativeTime(connection.last_sync_at)}</div>
          </StatusCard>
          <StatusCard label="Next Sync" subtext={connection.next_sync_at ? new Date(connection.next_sync_at).toLocaleString() : null}>
            <div className="text-base font-semibold text-text">{formatRelativeTime(connection.next_sync_at)}</div>
          </StatusCard>
        </div>

        {/* Two Column Layout */}
        <div className="flex gap-6 mb-6">
          {/* Left: Credentials */}
          <div className="flex-1 bg-card border border-card-bd rounded-card overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3 border-b border-border">
              <span className="text-base font-semibold text-text">Credentials</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Auth Method</div>
                <div className="text-sm text-text">{sourceType?.credentialLabel || connection.auth_method}</div>
              </div>
              {connection.credential_display && (
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">
                    {sourceType?.authMethod === "api_key" ? "API Key" : "Credential"}
                  </div>
                  <div className="text-sm text-text font-mono flex items-center gap-2">
                    {showCredential ? connection.credential_display : maskCredential(connection.credential_display)}
                    <button
                      className="text-text-2 hover:text-text text-xs"
                      onClick={() => setShowCredential(!showCredential)}
                      aria-label={showCredential ? "Hide credential" : "Show credential"}
                    >
                      {showCredential
                        ? <FontAwesomeIcon icon={icon({ name: "eye-slash", style: "light" })} />
                        : <FontAwesomeIcon icon={icon({ name: "eye", style: "light" })} />
                      }
                    </button>
                  </div>
                </div>
              )}
              {Object.entries(connection.metadata || {}).map(([key, value]) => (
                value && (
                  <div key={key}>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">
                      {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </div>
                    <div className="text-sm text-text">{value}</div>
                  </div>
                )
              ))}
              {dataTypeLabels.length > 0 && (
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Data Imported</div>
                  <div className="flex gap-1.5 flex-wrap">
                    {dataTypeLabels.map((label) => (
                      <span key={label} className="px-2 py-0.5 rounded text-xs bg-surface-2 text-text-2 border border-border">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Linked Import Job */}
          <div className="flex-1 bg-card border border-card-bd rounded-card overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3 border-b border-border">
              <span className="text-base font-semibold text-text">Linked Import Job</span>
              {connection.import_job_id && (
                <Link to={`/data-pipeline/${connection.import_job_id}`} className="text-xs text-primary font-medium hover:underline">
                  Open in Data Pipeline →
                </Link>
              )}
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Job Name</div>
                <div className="text-sm text-text">{connection.import_job_name || "—"}</div>
              </div>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Schedule</div>
                <div className="text-sm text-text">{connection.schedule_expression ? cronToHuman(connection.schedule_expression) : "—"}</div>
              </div>
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-1">Pipeline</div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="px-2 py-1 rounded bg-primary text-white font-medium">Import</span>
                  <span className="text-text-3">→</span>
                  <span className="px-2 py-1 rounded bg-surface-2 text-text-2 font-medium">Transform</span>
                  <span className="text-text-3">→</span>
                  <span className="px-2 py-1 rounded bg-surface-2 text-text-2 font-medium">Forecast</span>
                </div>
              </div>

              {connection.recent_runs && connection.recent_runs.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-text-2 mb-2">Recent Runs</div>
                  <div className="flex flex-col gap-1.5">
                    {connection.recent_runs.slice(0, 3).map((run, i) => (
                      <div key={i} className="flex justify-between items-center text-xs px-2 py-1.5 bg-surface-2 rounded">
                        <span className="text-text-2">{new Date(run.started_at).toLocaleString()}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-text-2">{formatDuration(run.duration_ms)}</span>
                          <span className={run.status === "success" ? "text-success font-medium" : "text-danger font-medium"}>
                            {run.status === "success" ? "Success" : "Failed"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Validation History */}
        <div className="bg-card border border-card-bd rounded-card overflow-hidden mb-6">
          <div className="px-5 py-3 border-b border-border">
            <span className="text-base font-semibold text-text">Validation History</span>
          </div>
          <div className="p-0">
            <ValidationHistory history={validationHistory || []} />
          </div>
        </div>
      </div>

      <Modal
        show={showDeleteModal}
        close={() => setShowDeleteModal(false)}
        title="Delete Connection"
        description={`Are you sure you want to delete "${connection?.connection_name}"? This will also remove the linked import job.`}
        confirmButton="Delete"
        confirmAction={() => deleteMutation.mutate()}
        size="sm"
      />
    </div>
  );
};

export default ConnectionDetailPage;
